/**
 * 羽归阁博客系统 - 文章API服务
 * 真实对接Parse Server的API服务
 */

import { parseCreate, parseUpdate, parseDelete } from './client';
import { PostAPI } from '@/types/api';
import { Post as BlogPost } from '@/types';
import { normalizeAssetUrl, normalizeAssetUrlsInText } from '@/utils/asset';

/** 访客 IP 缓存（未登录时点赞/查状态用，与评论逻辑一致） */
let cachedClientIp: string | null = null;

const isProd = import.meta.env.PROD;
const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';

/** 后端根地址（与 Parse 同源，用于 /api/ip） */
function getApiBaseUrl(): string {
  const url =
    import.meta.env.VITE_API_URL ||
    (isProd
      ? import.meta.env.VITE_PROD_BASE_URL || runtimeOrigin
      : import.meta.env.VITE_DEV_BASE_URL || 'http://localhost:1337') ||
    import.meta.env.VITE_PARSE_SERVER_URL ||
    (isProd
      ? import.meta.env.VITE_PROD_PARSE_SERVER_URL || `${runtimeOrigin}/parse`
      : import.meta.env.VITE_DEV_PARSE_SERVER_URL || 'http://localhost:1337/parse');
  return url.replace(/\/parse\/?$/, '') || runtimeOrigin || 'http://localhost:1337';
}

/** 获取当前访客 IP（请求本后端 /api/ip，不依赖第三方） */
export async function getClientIp(): Promise<string | null> {
  if (cachedClientIp) return cachedClientIp;
  try {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/ip`, { signal: AbortSignal.timeout(3000) });
    const data = await res.json();
    if (data?.ip) {
      cachedClientIp = String(data.ip).trim().slice(0, 128);
      return cachedClientIp;
    }
  } catch (_) {
    // 忽略失败，未传 clientIp 时后端会按无访客处理
  }
  return null;
}

/**
 * 兼容旧版 Post 类型（包含 likeCount 字段）
 */
export type Post = BlogPost & { likeCount?: number };
export interface CategoryDetail {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
}

export interface CategoryPostSummary {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  coverImage?: string;
  publishedAt?: string | Date;
  views: number;
  likes: number;
}

export interface CategoryPostsResponse {
  data: CategoryPostSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ArchivePostSummary {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  coverImage?: string;
  publishedAt?: string | Date;
  updatedAt?: string | Date;
  views: number;
  likes: number;
  commentCount: number;
  categories: Array<{ id: string; name: string; slug: string; color: string }>;
  tags: string[];
}

export interface BlogSiteStats {
  totalPosts: number;
  postsIn30Days: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
}

export class PostApiService {
  private static async fetchNoStore(input: string): Promise<Response> {
    return fetch(input, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
  }

  private static async fetchBlogPosts(params: Record<string, string | number | boolean | undefined>) {
    const base = getApiBaseUrl();
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });
    const res = await this.fetchNoStore(`${base}/api/blog/posts?${query.toString()}`);
    if (!res.ok) {
      throw new Error(`获取文章列表失败: ${res.status}`);
    }
    return res.json();
  }

  private static transformBlogPostData(item: any): Post {
    const categoryList = Array.isArray(item?.categories) ? item.categories : [];
    const categories = categoryList.map((cat: any) => ({
      id: cat?.id || '',
      name: cat?.name || '',
      slug: cat?.slug || '',
      description: cat?.description || '',
      color: cat?.color || '',
      postCount: cat?.postCount || 0,
    })).filter((cat: any) => cat.id);

    const rawTags = Array.isArray(item?.tags) ? item.tags : [];
    const tags = rawTags.map((tag: any) => {
      if (typeof tag === 'string') {
        return {
          id: tag,
          name: tag,
          slug: tag,
          description: '',
          color: '',
          postCount: 0,
        };
      }
      return {
        id: tag?.id || tag?.objectId || '',
        name: tag?.name || '',
        slug: tag?.slug || '',
        description: tag?.description || '',
        color: tag?.color || '',
        postCount: tag?.postCount || 0,
      };
    }).filter((tag: any) => tag.id);

    return {
      id: item.id,
      title: item.title || '',
      slug: item.slug || '',
      content: normalizeAssetUrlsInText(item.content || ''),
      excerpt: item.excerpt || '',
      coverImage: normalizeAssetUrl(item.coverImage),
      status: item.status || 'draft',
      author: item.author
        ? {
            id: item.author.id || '',
            username: item.author.username || 'unknown',
            displayName: item.author.displayName || item.author.username || '未知作者',
            avatar: item.author.avatar || null,
          } as any
        : ({
            id: 'unknown',
            username: 'unknown',
            displayName: '未知作者',
            avatar: null,
          } as any),
      categories: categories as any,
      tags: tags as any,
      publishedAt: item.publishedAt,
      readTime: item.readTime,
      likes: item.likes || 0,
      likeCount: item.likes || 0,
      views: item.views || 0,
      comments: Array.isArray(item.comments) ? item.comments : [],
      metadata: item.metadata || {},
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  /**
   * 获取精选文章
   */
  static async getFeaturedPosts(limit = 6): Promise<Post[]> {
    try {
      const payload = await this.fetchBlogPosts({
        page: 1,
        limit,
        status: 'published',
        featured: true,
        sortBy: 'publishedAt',
        sortOrder: 'desc',
      });
      return (payload?.data || []).map((item: any) => this.transformBlogPostData(item));
    } catch (error) {
      console.error('获取精选文章失败:', error);
      throw error;
    }
  }

  /**
   * 获取热门文章
   */
  static async getPopularPosts(limit = 10): Promise<Post[]> {
    try {
      const payload = await this.fetchBlogPosts({
        page: 1,
        limit,
        status: 'published',
        sortBy: 'views',
        sortOrder: 'desc',
      });
      return (payload?.data || []).map((item: any) => this.transformBlogPostData(item));
    } catch (error) {
      console.error('获取热门文章失败:', error);
      throw error;
    }
  }

  /**
   * 获取最新文章
   */
  static async getRecentPosts(limit = 10): Promise<Post[]> {
    try {
      const payload = await this.fetchBlogPosts({
        page: 1,
        limit,
        status: 'published',
        sortBy: 'publishedAt',
        sortOrder: 'desc',
      });
      return (payload?.data || []).map((item: any) => this.transformBlogPostData(item));
    } catch (error) {
      console.error('获取最新文章失败:', error);
      throw error;
    }
  }

  /**
   * 根据ID获取文章
   */
  static async getPostById(id: string): Promise<Post | null> {
    try {
      const base = getApiBaseUrl();
      const res = await this.fetchNoStore(`${base}/api/blog/posts/${encodeURIComponent(id)}`);
      if (res.status === 404) {
        return null;
      }
      if (!res.ok) {
        throw new Error(`获取文章失败: ${res.status}`);
      }
      const post = await res.json();
      return this.transformBlogPostData(post);
    } catch (error) {
      console.error('获取文章失败:', error);
      return null;
    }
  }

  /**
   * 根据 slug 获取文章
   */
  static async getPostBySlug(slug: string): Promise<Post | null> {
    try {
      const base = getApiBaseUrl();
      const res = await this.fetchNoStore(`${base}/api/blog/posts/slug/${encodeURIComponent(slug)}`);
      if (res.status === 404) {
        return null;
      }
      if (!res.ok) {
        throw new Error(`根据 slug 获取文章失败: ${res.status}`);
      }
      const post = await res.json();
      return this.transformBlogPostData(post);
    } catch (error) {
      console.error('根据 slug 获取文章失败:', error);
      return null;
    }
  }

  static async getCategoryBySlug(slug: string): Promise<CategoryDetail | null> {
    try {
      const base = getApiBaseUrl();
      const res = await this.fetchNoStore(`${base}/api/blog/categories/slug/${encodeURIComponent(slug)}`);
      if (res.status === 404) {
        return null;
      }
      if (!res.ok) {
        throw new Error(`获取分类失败: ${res.status}`);
      }
      const data = await res.json();
      if (!data) {
        return null;
      }
      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        color: data.color,
      };
    } catch (error) {
      console.error('获取分类详情失败:', error);
      return null;
    }
  }

  static async getPostsByCategory(
    categoryId: string,
    page = 1,
    limit = 12,
  ): Promise<CategoryPostsResponse> {
    const base = getApiBaseUrl();
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const res = await this.fetchNoStore(`${base}/api/blog/categories/${categoryId}/posts?${params.toString()}`);
    if (!res.ok) {
      throw new Error(`获取分类文章失败: ${res.status}`);
    }
    const payload = await res.json();
    return {
      data: (payload?.data || []).map((item: any) => ({
        id: item.id,
        title: item.title,
        slug: item.slug,
        excerpt: item.excerpt,
        coverImage: normalizeAssetUrl(item.coverImage),
        publishedAt: item.publishedAt,
        views: item.views || 0,
        likes: item.likes || 0,
      })),
      pagination: payload?.pagination || {
        page,
        limit,
        total: 0,
        totalPages: 0,
      },
    };
  }

  static async getArchivePosts(limit = 200): Promise<ArchivePostSummary[]> {
    const base = getApiBaseUrl();
    const params = new URLSearchParams({
      page: '1',
      limit: String(limit),
      status: 'archived',
    });
    const res = await this.fetchNoStore(`${base}/api/blog/posts?${params.toString()}`);
    if (!res.ok) {
      throw new Error(`获取归档文章失败: ${res.status}`);
    }
    const payload = await res.json();
    return (payload?.data || []).map((item: any) => ({
      id: item.id,
      title: item.title,
      slug: item.slug,
      excerpt: item.excerpt,
      coverImage: normalizeAssetUrl(item.coverImage),
      publishedAt: item.publishedAt,
      updatedAt: item.updatedAt,
      views: item.views || 0,
      likes: item.likes || 0,
      commentCount: item.commentCount || 0,
      categories: Array.isArray(item.categories) ? item.categories : [],
      tags: Array.isArray(item.tags) ? item.tags : [],
    }));
  }

  static async getSiteStats(): Promise<BlogSiteStats> {
    const base = getApiBaseUrl();
    const res = await this.fetchNoStore(`${base}/api/blog/stats`);
    if (!res.ok) {
      throw new Error(`获取站点统计失败: ${res.status}`);
    }
    const payload = await res.json();
    return {
      totalPosts: Number(payload?.totalPosts || 0),
      postsIn30Days: Number(payload?.postsIn30Days || 0),
      totalViews: Number(payload?.totalViews || 0),
      totalLikes: Number(payload?.totalLikes || 0),
      totalComments: Number(payload?.totalComments || 0),
    };
  }

  /**
   * 创建文章
   */
  static async createPost(request: PostAPI.CreateRequest): Promise<Post> {
    try {
      const postData = {
        ...request,
        slug: this.generateSlug(request.title),
        publishedAt: request.status === 'published' ? new Date() : undefined
      };
      
      const post = await parseCreate('Posts', postData);
      return this.transformPostData(post);
    } catch (error) {
      console.error('创建文章失败:', error);
      throw error;
    }
  }

  /**
   * 更新文章
   */
  static async updatePost(request: PostAPI.UpdateRequest): Promise<Post> {
    try {
      const { id, ...updateData } = request;
      
      if (updateData.status === 'published' && !updateData.publishedAt) {
        updateData.publishedAt = new Date();
      }
      
      const post = await parseUpdate('Posts', id, updateData);
      return this.transformPostData(post);
    } catch (error) {
      console.error('更新文章失败:', error);
      throw error;
    }
  }

  /**
   * 删除文章
   */
  static async deletePost(id: string): Promise<void> {
    try {
      await parseDelete('Posts', id);
    } catch (error) {
      console.error('删除文章失败:', error);
      throw error;
    }
  }

  /**
   * 查询文章点赞数与当前用户/访客是否已点赞（不要求登录；未登录时传 clientIp 查访客状态）
   * 云函数 404/141 时退化为仅读 likes 字段，isLiked 为 false
   */
  static async getPostLikeStatus(postId: string): Promise<{ likes: number; isLiked: boolean }> {
    try {
      const base = getApiBaseUrl();
      const res = await this.fetchNoStore(`${base}/api/blog/posts/${encodeURIComponent(postId)}/like-status`);
      if (!res.ok) throw new Error(`获取点赞状态失败: ${res.status}`);
      const result = await res.json();
      return {
        likes: Number(result?.likes || 0),
        isLiked: result?.isLiked === true,
      };
    } catch (err: unknown) {
      console.warn('getPostLikeStatus 失败:', (err as Error)?.message ?? err);
      return { likes: 0, isLiked: false };
    }
  }

  /**
   * 点赞文章（不需登录；未登录时按 IP 生成访客用户并写入 PostLike）
   * 云函数 404/141 时退化为直接对 Posts 的 likes 字段 +1
   */
  static async likePost(id: string): Promise<{ likes: number; isLiked: boolean }> {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/blog/posts/${encodeURIComponent(id)}/like`, {
      method: 'POST',
    });
    if (!res.ok) {
      throw new Error(`点赞失败: ${res.status}`);
    }
    const result = await res.json();
    return {
      likes: Number(result?.likes || 0),
      isLiked: result?.isLiked === true,
    };
  }

  /**
   * 取消点赞（不需登录；按 user 或访客 IP 删除 PostLike）
   * 云函数 404/141 时退化为直接对 Posts 的 likes 字段 -1
   */
  static async unlikePost(id: string): Promise<{ likes: number; isLiked: boolean }> {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/blog/posts/${encodeURIComponent(id)}/unlike`, {
      method: 'POST',
    });
    if (!res.ok) {
      throw new Error(`取消点赞失败: ${res.status}`);
    }
    const result = await res.json();
    return {
      likes: Number(result?.likes || 0),
      isLiked: result?.isLiked === true,
    };
  }

  /**
   * 增加文章浏览量
   *
   * 说明：
   * - 浏览量不是核心业务，不应影响页面正常渲染
   * - 当前本地 / Docker 环境下，Parse 数据与 REST/SKD 配置可能不完全一致，导致 404 / Object not found
   * - 为避免频繁在控制台刷错误，这里只在云函数可用时尝试调用；否则直接跳过
   */
  static async incrementViews(id: string): Promise<void> {
    if (!id || typeof id !== 'string') return;
    const base = getApiBaseUrl();
    await fetch(`${base}/api/blog/posts/${encodeURIComponent(id)}/views/increment`, {
      method: 'POST',
    }).catch(() => undefined);
  }

  /**
   * 生成文章slug
   */
  private static generateSlug(title: string): string {
    return title
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, '-')
      .replace(/[^\w\-\u4e00-\u9fff]+/g, '')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * 转换Parse对象为Post类型
   */
  private static transformPostData(parseObject: any): Post {
    const rawCategories = parseObject.get('categories') || [];
    const categories = rawCategories.map((cat: any) => {
      if (typeof cat === 'string') {
        return {
          id: cat,
          name: cat,
          slug: cat,
          description: '',
          color: '',
          postCount: 0,
        };
      }
      return {
        id: cat.id,
        name: cat.get('name'),
        slug: cat.get('slug'),
        description: cat.get('description'),
        color: cat.get('color'),
        postCount: cat.get('postCount') || 0,
      };
    });

    const rawTags = parseObject.get('tags') || [];
    const tags = rawTags.map((tag: any) => {
      if (typeof tag === 'string') {
        return {
          id: tag,
          name: tag,
          slug: tag,
          description: '',
          color: '',
          postCount: 0,
        };
      }
      return {
        id: tag.id,
        name: tag.get('name'),
        slug: tag.get('slug'),
        description: tag.get('description'),
        color: tag.get('color'),
        postCount: tag.get('postCount') || 0,
      };
    });

    return {
      id: parseObject.id,
      title: parseObject.get('title'),
      slug: parseObject.get('slug'),
      content: normalizeAssetUrlsInText(parseObject.get('content')),
      excerpt: parseObject.get('excerpt'),
      coverImage: normalizeAssetUrl(parseObject.get('coverImage')),
      status: parseObject.get('status'),
      author: parseObject.get('author') ? {
        id: parseObject.get('author').id,
        username: parseObject.get('author').get('username'),
        displayName: parseObject.get('author').get('displayName'),
        avatar: parseObject.get('author').get('avatar')
      } : null,
      categories,
      tags,
      publishedAt: parseObject.get('publishedAt'),
      readTime: parseObject.get('readTime'),
      likes: parseObject.get('likes') || 0,
      likeCount: parseObject.get('likes') || 0,
      views: parseObject.get('views') || 0,
      comments: parseObject.get('comments') || [],
      metadata: parseObject.get('metadata') || {},
      createdAt: parseObject.get('createdAt'),
      updatedAt: parseObject.get('updatedAt')
    };
  }

}

// 兼容性导出
export const PostService = PostApiService;
export const postApi = PostApiService;

// 默认导出
export default PostApiService;
